import { Box, Divider, Grid, List, ListItem, Paper, Skeleton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import React from 'react'
import PropTypes from 'prop-types'
import { type ConnectedYahooFantasyPlayer } from 'features/fantasy-team/models/yahooConnectedTeam'
import type YahooFantasyPlayerStatistics from 'features/fantasy-team/models/yahooFantasyPlayerStatistics'
import { useQuery } from '@tanstack/react-query'
import { ApiQueryKeys, ApiQueryService } from 'services/query/apiQueryService'
import FantasyProfileHeader from './FantasyProfileHeader'
import SportsHockeyIcon from '@mui/icons-material/SportsHockey'
import { type PlayerProfileReportDto } from 'features/profile/models/playerProfileReportDto'
import TeamImage from 'components/shared-components/TeamImage'

interface PlayerStatsSwipeableDrawerProps {
  open: boolean
  height?: number | string
  onOpen: () => void
  player: ConnectedYahooFantasyPlayer
  onClose: () => void
}

const PlayerStatsSwipeableDrawer: React.FC<PlayerStatsSwipeableDrawerProps> = ({ open, onClose, onOpen, height, player }) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const { isFetching, data: playerStatistics, refetch: getPlayerStatistics } = useQuery<YahooFantasyPlayerStatistics>({
    queryKey: [ApiQueryKeys.GetYahooFantasyPlayerStatistics],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.GetYahooFantasyPlayerStatistics](player.playerId)).data
    },
    enabled: !(player.playerId === 0)
  })

  const { data: fantaHockeyScore, refetch: getFantaHockeyPlayerScore } = useQuery<number>({
    queryKey: [ApiQueryKeys.GetPlayerFantaHockeyScore],
    queryFn: async () => {
      return (await ApiQueryService[ApiQueryKeys.GetPlayerFantaHockeyScore](player.playerId)).data
    },
    enabled: !(player.playerId === 0)
  })

  const { data: playerReports, isFetching: isReportsFetching, refetch: getPlayerReports } = useQuery<PlayerProfileReportDto>({
    queryKey: [ApiQueryKeys.GetPlayerReportsQuery],
    queryFn: async () => {
      const data = (await ApiQueryService[ApiQueryKeys.GetPlayerReportsQuery](player.playerId)).data
      return data
    },
    enabled: !(player.playerId === 0) && player.position !== 'G'
  })

  React.useEffect(() => {
    const fetchPlayerStatistics = async (): Promise<void> => {
      if (player.playerId !== 0) {
        await Promise.all([
          getPlayerStatistics(),
          getFantaHockeyPlayerScore(),
          getPlayerReports()
        ])
      }
    }
    void fetchPlayerStatistics()
  }, [player])

  const FantasyPlayerProfileReport = React.memo((): JSX.Element | null => {
    if ((playerReports?.lastFiveGamePerformanceSummary?.length === 0 || playerReports === null || playerReports === undefined || playerReports?.upcomingGamesMatchupAnalysis?.length === 0)) {
      return <></>
    }

    return (
      <Paper
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          p: 2,
          my: 2,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, rgba(0,0,0,0.03))`,
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        {/* decorative puck */}
        <Box
          sx={{
            position: 'absolute',
            right: -40,
            top: -40,
            width: 160,
            height: 160,
            borderRadius: '50%',
            bgcolor: (t) => t.palette.primary.main,
            opacity: 0.06,
            transform: 'rotate(15deg)'
          }}
        />

        <Stack direction="row" spacing={1} alignItems="center" mb={1} sx={{ position: 'relative', zIndex: 1 }}>
          <SportsHockeyIcon color="primary" />
          <Typography variant="subtitle2" color="text.primary" sx={{ fontWeight: 800, letterSpacing: 0.4 }}>
            Fantasy Player Report
          </Typography>
        </Stack>

        <Divider sx={{ my: 1, position: 'relative', zIndex: 1 }} />
        {isReportsFetching && (
          <Box display='flex' flexDirection='column' alignItems='center'>
            <Box my={1} />
              <Skeleton variant='text' width='100%' height={20} />
              <Skeleton variant='text' width='100%' height={20} />
              <Skeleton variant="text" width='100%' height={20} />
            </Box>
        )}

        {!isReportsFetching && playerReports?.lastFiveGamePerformanceSummary?.length !== undefined && (
        <Grid container spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Last 5 Games</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <List>
              {playerReports != null && playerReports.lastFiveGamePerformanceSummary.length === 0 && (
                <Typography variant="body2" color="text.secondary">No recent game performance data available.</Typography>
              )}
              {playerReports?.lastFiveGamePerformanceSummary?.map((s, idx) => (
                <Box key={idx} my={1}>
                    <ListItem>
                      <TeamImage src={s.team} height={24} /> <Typography>vs</Typography> <TeamImage src={s.opponentTeam} height={24} />
                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
                      {s.fantasyPoints} pts @ {s.date}
                      </Typography>
                    </ListItem>
                </Box>
              ))}
              </List>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Predicted Fantasy Performance</Typography>
            <Paper variant="outlined" sx={{ p: 1.25, borderLeft: `4px solid ${theme.palette.primary.main}`, bgcolor: (t) => t.palette.action.hover }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                {playerReports?.upcomingGamesMatchupAnalysis.map((s, idx) => (
                <Box key={idx} my={1}>
                    <ListItem>
                      <TeamImage src={s.team} height={24} /> <Typography>vs</Typography> <TeamImage src={s.opponentTeam} height={24} />
                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
                      {s.projectedFantasyPoints} pts @ {s.date}
                      </Typography>
                    </ListItem>
                </Box>))}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>Analytics Insights</Typography>
            <Paper sx={{ p: 2, mt: 1 }}>
              <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                {playerReports?.summaryInsights ?? 'No insights available.'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>)}

        {!isReportsFetching && (
          <Stack direction="row" spacing={1} mt={2} alignItems="center" justifyContent="flex-end" sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="caption" color="text.secondary">Last Updated</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              {new Date().toLocaleDateString()}
            </Typography>
          </Stack>)}
      </Paper>
    )
  })
  FantasyPlayerProfileReport.displayName = 'FantasyPlayerProfileReport'

  const statCard = (title: string, value: React.ReactNode, subtitle?: string, accent?: string): JSX.Element => {
    const color = accent ?? theme.palette.primary.main
    return (
      <Paper elevation={6} sx={{
        flex: 1,
        minWidth: 65,
        p: 1,
        borderRadius: 2,
        bgcolor: 'background.paper',
        textAlign: 'center',
        borderLeft: `6px solid ${color}`,
        boxShadow: `0 8px 24px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(14,30,37,0.08)'}`
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 900, color }}>{value ?? '-'}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{title}</Typography>
        {subtitle != null && subtitle !== '' && (
          <Typography variant="caption" display="block" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Paper>
    )
  }

  return (
    <FantasyProfileHeader isFetching={isFetching} open={open} onClose={onClose} onOpen={onOpen} fantaHockeyScore={fantaHockeyScore} height={height} player={player} playerStatistics={playerStatistics}>
      {playerReports != null && playerReports !== undefined && playerReports?.lastFiveGamePerformanceSummary?.length > 0 && playerReports?.upcomingGamesMatchupAnalysis?.length > 0 && (
        <FantasyPlayerProfileReport />
      )}
      <Paper sx={{ boxShadow: 'none', mt: 3, p: isSmall ? 1 : 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={12}>
            <Paper sx={{ p: 0, bgcolor: 'transparent' }}>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  {statCard('Goals', playerStatistics?.goals ?? '-', '', theme.palette.success.main)}
                </Grid>
                <Grid item xs={4}>
                  {statCard('Assists', playerStatistics?.assists ?? '-', '', theme.palette.info.main)}
                </Grid>
                <Grid item xs={4}>
                  {statCard('Points', playerStatistics?.points ?? '-', '')}
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={1} alignItems="center">
                <Grid item m={2} p={2} xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Shots Taken</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" sx={{ width: 36 }}>{playerStatistics?.shots ?? '-'}</Typography>
                    </Stack>
                  </Stack>
                </Grid>

                <Grid item m={2} p={2} xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="subtitle2">Shooting Percentage</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" sx={{ width: 72 }}>{playerStatistics?.shootingPercentage?.toFixed(2) ?? '-' }%</Typography>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          {[
            { title: 'Faceoff Pct', value: playerStatistics?.faceoffWinPercentage?.toFixed(2) ?? '-' + '%', tooltip: 'Faceoff Win Percentage' },
            { title: 'Avg. TOI Per Game', value: ((playerStatistics?.timeOnIcePerGame) != null) ? `${playerStatistics.timeOnIcePerGame?.toFixed(2)} min` : '-', tooltip: 'Time on Ice per game' },
            { title: 'PP TOI Per Game', value: ((playerStatistics?.ppTimeOnIcePerGame) != null) ? `${playerStatistics.ppTimeOnIcePerGame?.toFixed(2)} min` : '-', tooltip: 'Power play TOI' },
            { title: 'SH TOI Per Game', value: ((playerStatistics?.shTimeOnIcePerGame) != null) ? `${playerStatistics.shTimeOnIcePerGame?.toFixed(2)} min` : '-', tooltip: 'Shorthanded TOI' },
            { title: 'Hits', value: playerStatistics?.hits ?? '-', tooltip: 'Hits' },
            { title: 'Takeaways', value: playerStatistics?.takeaways ?? '-', tooltip: 'Takeaways' },
            { title: 'EV TOI Per Game', value: ((playerStatistics?.evTimeOnIcePerGame) != null) ? `${playerStatistics.evTimeOnIcePerGame?.toFixed(2)} min` : '-', tooltip: 'Even Time on Ice' },
            { title: 'Power Play Goals', value: playerStatistics?.powerPlayGoals ?? '-', tooltip: 'Power Play Goals' },
            { title: 'ICF', value: playerStatistics?.individualFenwickFor ?? '-', tooltip: 'Individual Fenwick For' },
            { title: 'Overtime Goals', value: playerStatistics?.overtimeGoals ?? '-', tooltip: 'Overtime Goals' }
          ].map((s, idx) => (
            <Grid item xs={6} sm={4} md={2} key={idx}>
              <Tooltip title={s.tooltip ?? ''} placement="bottom">
                <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{s.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.title}</Typography>
                </Paper>
              </Tooltip>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Per 60 minute Metrics</Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="h6">{playerStatistics?.goalsPer60?.toFixed(2) ?? '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">Goals Per 60 min</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h6">{playerStatistics?.assistsPer60?.toFixed(2) ?? '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">Assists Per 60 min</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h6">{playerStatistics?.pointsPer60?.toFixed(2) ?? '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">Points Per 60 min</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Defensive Actions</Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="h6">{playerStatistics?.blockedShots ?? '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">Blocked</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h6">{playerStatistics?.shotAttemptsBlocked ?? '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">Shot Attempts</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h6">{playerStatistics?.individualCorsiFor ?? '-'}</Typography>
                  <Typography variant="caption" color="text.secondary">Individual Corsi For</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Game Winning Goals</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{playerStatistics?.gameWinningGoals ?? 0}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Penalty Minutes</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{playerStatistics?.penaltyMinutes ?? 0}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2">Plus / Minus</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{playerStatistics?.plusMinus ?? '-'}</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </FantasyProfileHeader>
  )
}

PlayerStatsSwipeableDrawer.displayName = 'PlayerStatsSwipeableDrawer'
PlayerStatsSwipeableDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

export default React.memo(PlayerStatsSwipeableDrawer)
